import React from 'react';
import { makeStyles } from 'design-system/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { captureClientSideError } from '@scale/frontend-lib/errors';

interface ErrorPlaceholderProps {
  errorId: string | undefined;
  errorMessage: string | undefined;
  _errorInfo: any;
  componentDisplayName: string;
  hint?: string;
}

const ErrorPlaceholder: React.FC<ErrorPlaceholderProps> = ({
  errorId,
  errorMessage,
  _errorInfo,
  componentDisplayName,
  hint,
}) => {
  const classes = useStyles();
  return (
    <div className={classes.errorContainer}>
      <div className={classes.errorIcon}>
        <FontAwesomeIcon className={classes.icon} icon="exclamation-triangle" />
      </div>
      <div key={componentDisplayName} className={classes.errorContent}>
        <h2>An unexpected error has occurred</h2>
        {hint ? (
          <div>{hint}</div>
        ) : (
          <div>Hint: either the task's taxonomy is malformed or the asset has failed to load.</div>
        )}
        {errorId && <div>Error ID: {errorId}</div>}
        {errorMessage && <div>Error Message: {errorMessage}</div>}
        {/* Stack trace: */}
        {/* {errorInfo && errorInfo.componentStack && <p>Error Info: {errorInfo.componentStack}</p>} */}
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  errorContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  errorIcon: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    margin: theme.spacing(2),
  },
  icon: { fontSize: 32, color: theme.palette.warning.main },
  errorContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

interface IErrorBoundaryProps {
  hint?: string;
  children?: React.ReactNode;
}
class ErrorBoundary extends React.Component<IErrorBoundaryProps> {
  state: {
    error?: boolean;
    errorInfo?: any;
    errorMessage?: string;
    errorId?: string;
  } = {};

  componentDidCatch(error: any, info: any) {
    const errorId = captureClientSideError(error, 'generic', info);

    this.setState({
      errorId,
      error: true,
      errorInfo: info,
      errorMessage: error && error.message,
    });
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorPlaceholder
          errorId={this.state.errorId}
          errorMessage={this.state.errorMessage}
          _errorInfo={this.state.errorInfo}
          componentDisplayName="unknown"
          hint={this.props.hint}
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
